import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { cn } from '@/utils/cn';
import {
  AgentFormValues,
  PromptCategory,
  PROMPT_CATEGORIES,
  PROMPT_VARIABLES,
  TENANT_ROLES,
  screenMeta,
  areaCls,
  UNREACHABLE_MODULES,
  SCREEN_META,
} from './agentShared';
import { Button } from '@/components/UI/Button';
import { fieldClass } from '@/components/UI/fieldStyles';

// ── RolePicker (inline panel) ──────────────────────────────────────────────────

const RolePicker: React.FC<{
  selected: string[];
  onChange: (roles: string[]) => void;
  onDone: () => void;
}> = ({ selected, onChange, onDone }) => {
  const toggle = (role: string) => {
    onChange(selected.includes(role) ? selected.filter((r) => r !== role) : [...selected, role]);
  };

  return (
    <div className="border-b border-ui-border bg-ui-card px-4 py-3">
      <p className="mb-2 text-xs text-ui-text3">
        Block is injected only for the selected roles. Leave empty to inject for everyone.
      </p>
      <div className="flex flex-wrap gap-2">
        {TENANT_ROLES.map((role) => {
          const checked = selected.includes(role.value);
          return (
            <label
              key={role.value}
              className={cn(
                'inline-flex cursor-pointer select-none items-center gap-1.5 rounded-chip border px-2.5 py-1 text-xs transition-colors',
                checked
                  ? 'border-accent/30 bg-accent/5 font-medium text-ui-text'
                  : 'border-ui-border bg-ui-card text-ui-text2 hover:bg-ui-hover',
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(role.value)}
                className="h-3 w-3 accent-accent"
              />
              {role.label}
            </label>
          );
        })}
      </div>
      <div className="mt-2.5 flex items-center justify-between">
        {selected.length > 0 ? (
          <Button type="button" variant="link" size="xs" className="text-danger-text" onClick={() => onChange([])}>
            Clear — always inject
          </Button>
        ) : (
          <span className="text-xs text-ui-text3">No restriction — always injected</span>
        )}
        <Button type="button" variant="link" size="xs" className="text-accent-text" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
};

// ── ScreenPicker (inline panel) ─────────────────────────────────────────────────

const ScreenPicker: React.FC<{
  selected: string[];
  onChange: (modules: string[]) => void;
  onDone: () => void;
}> = ({ selected, onChange, onDone }) => {
  const ALL_SCREENS = Object.keys(SCREEN_META).filter((m) => !UNREACHABLE_MODULES.has(m));
  const toggle = (module: string) => {
    onChange(selected.includes(module) ? selected.filter((m) => m !== module) : [...selected, module]);
  };

  return (
    <div className="border-b border-ui-border bg-ui-card px-4 py-3">
      <p className="mb-2 text-xs text-ui-text3">
        Block is injected only while the selected screen(s) are open. Leave empty to inject on every screen.
      </p>
        <div className="flex flex-wrap gap-2">
          {ALL_SCREENS.map((module) => {
            const checked = selected.includes(module);
            const meta = screenMeta(module);
            return (
              <label
                key={module}
                title={meta.description}
                className={cn(
                  'inline-flex cursor-pointer select-none items-center gap-1.5 rounded-chip border px-2.5 py-1 text-xs transition-colors',
                  checked
                    ? 'border-accent/30 bg-accent/5 font-medium text-ui-text'
                    : 'border-ui-border bg-ui-card text-ui-text2 hover:bg-ui-hover',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(module)}
                  className="h-3 w-3 accent-accent"
                />
                {meta.label}
              </label>
            );
          })}
        </div>
      <div className="mt-2.5 flex items-center justify-between">
        {selected.length > 0 ? (
          <Button type="button" variant="link" size="xs" className="text-danger-text" onClick={() => onChange([])}>
            Clear — always inject
          </Button>
        ) : (
          <span className="text-xs text-ui-text3">No restriction — always injected</span>
        )}
        <Button type="button" variant="link" size="xs" className="text-accent-text" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
};

// ── PromptBlock ────────────────────────────────────────────────────────────────

interface PromptBlockProps {
  index: number;
  total: number;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  /** Show the "+ screens" module-scoping affordance. Default true (Instructions tab). The Modules
   * tab passes false — a block edited from a screen's own card already has its module fixed by
   * context (home_module or topic_modules, set by the caller), so re-scoping it from here would be
   * redundant and confusing. Every other part of the editor (tag, roles, variable chips) is
   * identical either way. */
  showScreenPicker?: boolean;
}

const PromptBlock: React.FC<PromptBlockProps> = ({ index, total, onMove, onRemove, showScreenPicker: allowScreenPicker = true }) => {
  const { register, watch, setValue } = useFormContext<AgentFormValues>();
  const [showCatPicker, setShowCatPicker] = useState(false);
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [showScreenPicker, setShowScreenPicker] = useState(false);
  const [customTag, setCustomTag] = useState('');

  const category = watch(`system_prompts.${index}.category`) as PromptCategory | undefined;
  const conditionRoles = (watch(`system_prompts.${index}.condition_roles`) as string[] | undefined) ?? [];
  const conditionModules = (watch(`system_prompts.${index}.condition_modules`) as string[] | undefined) ?? [];
  const catCfg = category ? PROMPT_CATEGORIES[category] : null;

  // ── variable insertion ─────────────────────────────────────────────────────

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { ref: rhfRef, onChange, onBlur, name } = register(`system_prompts.${index}.prompt`);

  const combineRef = (el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
    if (typeof rhfRef === 'function') {
      rhfRef(el);
    } else if (rhfRef) {
      (rhfRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
    }
  };

  const insertVariable = (token: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const snippet = `{{ ${token} }}`;
    const newValue = el.value.slice(0, start) + snippet + el.value.slice(end);
    setValue(`system_prompts.${index}.prompt`, newValue, { shouldDirty: true });
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const pos = start + snippet.length;
      textareaRef.current.setSelectionRange(pos, pos);
      textareaRef.current.focus();
    });
  };

  // ── setters ────────────────────────────────────────────────────────────────

  const setCategory = (cat: PromptCategory | undefined) => {
    setValue(`system_prompts.${index}.category`, cat as never, { shouldDirty: true });
    setShowCatPicker(false);
  };

  const setRoles = (roles: string[]) => {
    setValue(`system_prompts.${index}.condition_roles`, roles as never, { shouldDirty: true });
  };

  const setModules = (modules: string[]) => {
    setValue(`system_prompts.${index}.condition_modules`, modules as never, { shouldDirty: true });
  };

  const toggleCatPicker = () => { setShowCatPicker((p) => !p); setShowRolePicker(false); setShowScreenPicker(false); };
  const toggleRolePicker = () => { setShowRolePicker((p) => !p); setShowCatPicker(false); setShowScreenPicker(false); };
  const toggleScreenPicker = () => { setShowScreenPicker((p) => !p); setShowCatPicker(false); setShowRolePicker(false); };

  // ── role / screen badge labels ────────────────────────────────────────────

  const roleBadgeLabel =
    conditionRoles.length > 0
      ? conditionRoles
          .map((r) => TENANT_ROLES.find((t) => t.value === r)?.label ?? r)
          .join(', ')
      : null;

  const screenBadgeLabel =
    conditionModules.length > 0
      ? conditionModules.map((m) => screenMeta(m).label).join(', ')
      : null;

  return (
    <div className="overflow-hidden rounded-card border border-ui-border">

      {/* ── header ── */}
      <div className="flex items-center gap-2 border-b border-ui-border bg-ui-subtle px-3 py-2.5">
        {/* move */}
        <div className="flex shrink-0 flex-col gap-0.5">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0}
            className="text-xs leading-none text-ui-text3 hover:text-ui-text2 focus-ring disabled:opacity-25">▲</button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1}
            className="text-xs leading-none text-ui-text3 hover:text-ui-text2 focus-ring disabled:opacity-25">▼</button>
        </div>

        {/* category chip */}
        <button
          type="button"
          onClick={toggleCatPicker}
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium transition',
            catCfg
              ? catCfg.chip
              : category
              ? 'border-ui-border bg-ui-subtle text-ui-text2'
              : 'border-ui-border bg-ui-card text-ui-text3 hover:border-ui-border',
          )}
        >
          {catCfg && <span className={cn('h-1.5 w-1.5 rounded-full', catCfg.dot)} />}
          {catCfg ? catCfg.label : category ? category : '+ tag'}
          <span className="text-xs opacity-50">▾</span>
        </button>

        {/* role condition badge */}
        {roleBadgeLabel ? (
          <button
            type="button"
            onClick={toggleRolePicker}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary/30 bg-primary-soft px-2 py-0.5 text-xs font-medium text-primary-text transition-colors hover:brightness-95"
          >
            <span className="text-xs">◎</span>
            {roleBadgeLabel}
          </button>
        ) : (
          <button
            type="button"
            onClick={toggleRolePicker}
            className="shrink-0 text-xs text-ui-text3 transition hover:text-ui-text2"
          >
            + roles
          </button>
        )}

        {/* screen condition badge — legacy condition_modules only; hidden when the caller already
            fixes the module by context (home_module/topic_modules set from the Modules tab) */}
        {allowScreenPicker && (screenBadgeLabel ? (
          <button
            type="button"
            onClick={toggleScreenPicker}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent-text transition hover:bg-accent/15"
          >
            <span className="text-xs">▣</span>
            {screenBadgeLabel}
          </button>
        ) : (
          <button
            type="button"
            onClick={toggleScreenPicker}
            className="shrink-0 text-xs text-ui-text3 transition hover:text-ui-text2"
          >
            + screens
          </button>
        ))}

        {/* title — borderless fill */}
        <input
          className="min-w-0 flex-1 bg-transparent text-sm text-ui-text placeholder:text-ui-text3 focus:outline-none"
          placeholder="Title (optional)"
          {...register(`system_prompts.${index}.title`)}
        />

        {/* remove */}
        <Button type="button" variant="destructive-ghost" size="xs" className="shrink-0" onClick={onRemove}>
          Remove
        </Button>
      </div>

      {/* ── category picker ── */}
      {showCatPicker && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-ui-border bg-ui-card px-3 py-2.5">
          {(Object.entries(PROMPT_CATEGORIES) as [PromptCategory, typeof PROMPT_CATEGORIES[PromptCategory]][]).map(
            ([key, cfg]) => (
              <button
                key={key}
                type="button"
                onClick={() => setCategory(category === key ? undefined : key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition',
                  category === key ? cfg.chip : 'border-ui-border bg-ui-card text-ui-text2 hover:bg-ui-hover',
                )}
              >
                <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
                {cfg.label}
              </button>
            ),
          )}
          {category && (
            <button type="button" onClick={() => setCategory(undefined)}
              className="ml-1 text-xs text-ui-text3 hover:text-ui-text2">
              Clear
            </button>
          )}

          {/* Custom tag */}
          <div className="ml-auto flex items-center gap-1">
            <input
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const val = customTag.trim().toLowerCase().replace(/\s+/g, '_');
                  if (val) { setCategory(val as PromptCategory); setCustomTag(''); }
                }
              }}
              placeholder="custom tag…"
              className={cn(fieldClass({ size: 'xs' }), 'w-28')}
            />
            <Button
              type="button"
              variant="link"
              size="xs"
              className="text-accent-text"
              onClick={() => {
                const val = customTag.trim().toLowerCase().replace(/\s+/g, '_');
                if (val) { setCategory(val as PromptCategory); setCustomTag(''); }
              }}
            >
              Set
            </Button>
          </div>
        </div>
      )}

      {/* ── role picker ── */}
      {showRolePicker && (
        <RolePicker
          selected={conditionRoles}
          onChange={setRoles}
          onDone={() => setShowRolePicker(false)}
        />
      )}

      {/* ── screen picker ── */}
      {allowScreenPicker && showScreenPicker && (
        <ScreenPicker
          selected={conditionModules}
          onChange={setModules}
          onDone={() => setShowScreenPicker(false)}
        />
      )}

      {/* ── prompt body ── */}
      <div className="bg-ui-card p-3">
        <textarea
          ref={combineRef}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          rows={4}
          placeholder="Prompt content"
          className={cn(areaCls, 'font-mono text-sm leading-relaxed')}
        />

        {/* variable chips — grouped by User / Tenant / Session */}
        <div className="mt-2.5 space-y-1.5">
          {(['User', 'Tenant', 'Session'] as const).map((group) => {
            const vars = PROMPT_VARIABLES.filter((v) => v.group === group);
            return (
              <div key={group} className="flex flex-wrap items-center gap-1.5">
                <span className="w-14 shrink-0 text-xs font-medium text-ui-text3">{group}</span>
                {vars.map((v) => (
                  <button
                    key={v.token}
                    type="button"
                    onClick={() => insertVariable(v.token)}
                    title={`{{ ${v.token} }}`}
                    className="rounded-chip border border-ui-border bg-ui-subtle px-2 py-0.5 text-xs text-ui-text2 transition-colors hover:border-accent/40 hover:bg-accent/10 hover:text-accent-text"
                  >
                    {v.short}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PromptBlock;
